import React, { useState } from 'react';

// Segment palette — reused by the donut and the legend dots.
export const CHART_COLORS = [
  '#f5c542', '#4f6ef7', '#22c55e', '#ef8c43', '#38bdf8',
  '#a855f7', '#ec4899', '#14b8a6', '#f43f5e', '#84cc16',
  '#eab308', '#6366f1',
];

export const colorAt = i => CHART_COLORS[i % CHART_COLORS.length];

// ── Donut chart ──────────────────────────────────────────────────────────────
// Single-segment-per-stroke donut using stroke-dasharray. data = [{label, amount}].
export function DonutChart({ data, size = 220, thickness = 30, centerTop, centerMain }) {
  const total = data.reduce((s, d) => s + d.amount, 0);
  const r  = (size - thickness) / 2;
  const cx = size / 2, cy = size / 2;
  const c  = 2 * Math.PI * r;

  let offset = 0;
  const segments = total > 0 ? data.map((d, i) => {
    const len = (d.amount / total) * c;
    const seg = { len, offset, color: colorAt(i) };
    offset += len;
    return seg;
  }) : [];

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
      {/* Track */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--color-surface2)" strokeWidth={thickness} />
      {segments.map((s, i) => (
        <circle
          key={i}
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={s.color}
          strokeWidth={thickness}
          strokeDasharray={`${s.len} ${c - s.len}`}
          strokeDashoffset={-s.offset}
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      ))}
      {centerTop != null && (
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize="11" fill="var(--color-muted)">{centerTop}</text>
      )}
      {centerMain != null && (
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize="16" fontWeight="700" fill="var(--color-text)">{centerMain}</text>
      )}
    </svg>
  );
}

// ── Bar chart ────────────────────────────────────────────────────────────────
// bars = [{ label, value }]. Supports negative values via a zero baseline (used
// by the running account-balance chart). Hovering a bar shows a value tooltip.
// signColors=true colours each bar green when ≥ 0 and red when negative, so the
// user can read health at a glance (used for Account Balance).
const POS = 'var(--color-success)';
const NEG = 'var(--color-danger)';
export function BarChart({ bars, color = 'var(--color-accent)', signColors = false, formatMoney = v => v, height = 300 }) {
  const [hover, setHover] = useState(null);
  const values = bars.map(b => b.value);
  const maxV = Math.max(0, ...values);
  const minV = Math.min(0, ...values);
  const range = (maxV - minV) || 1;
  const zeroPct = ((0 - minV) / range) * 100;   // height of the zero line from the bottom
  const hasNeg  = minV < 0;
  // 5 evenly spaced axis ticks spanning the domain.
  const ticks = Array.from({ length: 5 }, (_, i) => minV + (range * i) / 4);

  // Axis value labels live in a fixed left gutter so bars never paint over them.
  const PAD = 72;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ position: 'relative', height }}>
        {/* Gridlines + axis labels (labels sit in the left gutter, left of the plot) */}
        {ticks.map((t, i) => (
          <div key={i} style={{
            position: 'absolute', left: PAD, right: 0, bottom: `${(i / 4) * 100}%`,
            borderTop: '1px solid var(--color-border)', pointerEvents: 'none',
          }}>
            <span style={{ position: 'absolute', right: 'calc(100% + 8px)', top: -7,
                            width: PAD - 10, textAlign: 'right', whiteSpace: 'nowrap',
                            fontSize: '0.62rem', color: 'var(--color-muted)' }}>
              {formatMoney(t)}
            </span>
          </div>
        ))}
        {/* Emphasised zero line when there are negative bars */}
        {hasNeg && (
          <div style={{ position: 'absolute', left: PAD, right: 0, bottom: `${zeroPct}%`,
                        borderTop: '1px solid var(--color-muted)', pointerEvents: 'none' }} />
        )}

        {/* Bars */}
        <div style={{ position: 'absolute', left: PAD, right: 0, top: 0, bottom: 0, display: 'flex', gap: '4%' }}>
          {bars.map((b, i) => {
            const pos    = b.value >= 0;
            const magPct = (Math.abs(b.value) / range) * 100;
            const active = hover === i;
            const fill   = signColors ? (pos ? POS : NEG) : color;
            return (
              <div
                key={i}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(h => (h === i ? null : h))}
                style={{ flex: 1, position: 'relative', cursor: 'pointer' }}
              >
                <div style={{
                  position: 'absolute', left: '50%', transform: 'translateX(-50%)',
                  width: '78%', maxWidth: 46,
                  bottom: pos ? `${zeroPct}%` : `${zeroPct - magPct}%`,
                  height: `${b.value === 0 ? 0 : Math.max(magPct, 0.6)}%`,
                  background: fill, opacity: hover == null || active ? 1 : 0.4,
                  borderRadius: pos ? '5px 5px 0 0' : '0 0 5px 5px',
                  transition: 'opacity 0.15s, height 0.25s ease',
                }} />
                {active && (
                  <div style={{
                    position: 'absolute', left: '50%', transform: 'translateX(-50%)',
                    bottom: `calc(${Math.max(zeroPct, zeroPct + (pos ? magPct : 0))}% + 8px)`,
                    whiteSpace: 'nowrap',
                    background: 'var(--color-surface2)', border: '1px solid var(--color-border)',
                    borderRadius: 6, padding: '0.3rem 0.5rem', fontSize: '0.7rem', color: 'var(--color-text)',
                    textAlign: 'center', zIndex: 2, boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
                  }}>
                    <div style={{ color: 'var(--color-muted)' }}>{b.label}</div>
                    <strong style={{ color: signColors ? fill : 'inherit' }}>{formatMoney(b.value)}</strong>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* X labels — aligned to the bars (same left gutter) */}
      <div style={{ display: 'flex', gap: '4%', marginTop: '0.4rem', paddingLeft: PAD }}>
        {bars.map((b, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: '0.6rem', color: 'var(--color-muted)' }}>
            {b.label}
          </div>
        ))}
      </div>
    </div>
  );
}
